import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/Login.css';
import BackButton from '../components/BackButton';

const Register = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [successMessages, setSuccessMessages] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSubmitError('');

    if (name === 'username') {
      validateUsername(value);
    } else if (name === 'password') {
      validatePassword(value);
      if (formData.confirmPassword) {
        validateConfirmPassword(value, formData.confirmPassword);
      }
    } else if (name === 'confirmPassword') {
      validateConfirmPassword(formData.password, value);
    }
  };

  const validateUsername = (username) => {
    const trimmed = username.trim();
    if (trimmed.length < 3) {
      setErrors(prev => ({ ...prev, username: 'El nombre de usuario debe tener al menos 3 caracteres' }));
      setSuccessMessages(prev => ({ ...prev, username: false }));
    } else if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
      setErrors(prev => ({ ...prev, username: 'Solo se permiten letras, números y guiones bajos' }));
      setSuccessMessages(prev => ({ ...prev, username: false }));
    } else {
      setErrors(prev => ({ ...prev, username: '' }));
      setSuccessMessages(prev => ({ ...prev, username: true }));
    }
  };

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password)
    };
    setPasswordRequirements(requirements);

    const allValid = Object.values(requirements).every(r => r);
    setErrors(prev => ({ ...prev, password: password.length > 0 && !allValid ? 'invalid' : '' }));
    return allValid;
  };

  const validateConfirmPassword = (password, confirmPassword) => {
    if (!confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: '' }));
      setSuccessMessages(prev => ({ ...prev, confirmPassword: false }));
      return false;
    }
    if (password !== confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: 'Las contraseñas no coinciden' }));
      setSuccessMessages(prev => ({ ...prev, confirmPassword: false }));
      return false;
    }
    setErrors(prev => ({ ...prev, confirmPassword: '' }));
    setSuccessMessages(prev => ({ ...prev, confirmPassword: true }));
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { username, password, confirmPassword } = formData;

    let isValid = true;
    if (username.trim().length < 3) { validateUsername(username); isValid = false; }
    if (!validatePassword(password)) isValid = false;
    if (!validateConfirmPassword(password, confirmPassword)) isValid = false;
    if (!isValid) return;

    setIsLoading(true);
    setSubmitError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/usuario/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre_usuario: username.trim(),
          password
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        if (response.status === 400 && errorData?.detail) {
          setSubmitError(errorData.detail);
        } else {
          setSubmitError('Error al crear la cuenta. Por favor, intenta nuevamente.');
        }
        return;
      }

      const data = await response.json();
      navigate('/iniciarsesion', {
        state: { successMessage: `¡Cuenta creada exitosamente! Bienvenido, ${data.nombre_usuario}.` }
      });

    } catch {
      setSubmitError('No se pudo conectar con el servidor. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    const hasChanges = formData.username || formData.password || formData.confirmPassword;
    if (hasChanges) {
      if (window.confirm('¿Estás seguro de que quieres salir? Se perderán todos los cambios.')) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const getFormGroupClass = (fieldName) => {
    let base = 'form-group';
    if (errors[fieldName] && errors[fieldName] !== '') base += ' error';
    else if (successMessages[fieldName]) base += ' success';
    return base;
  };

  const renderRequirement = (isValid, text) => (
    <div className={`requirement ${isValid ? 'valid' : 'invalid'}`}>
      <span className="requirement-icon">{isValid ? '✓' : '○'}</span>
      {text}
    </div>
  );

  return (
    <div className="login-container">
      <BackButton onClick={handleBack} />
      <div className="login-logo">
        <h1>Crear Cuenta</h1>
        <p>Únete a nuestra comunidad</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className={getFormGroupClass('username')}>
          <label htmlFor="username">Nombre de usuario *</label>
          <input
            type="text"
            id="username"
            name="username"
            placeholder="Ingresa tu nombre de usuario"
            value={formData.username}
            onChange={handleInputChange}
            required
          />
          {errors.username && <div className="error-message" style={{ display: 'block' }}>{errors.username}</div>}
          {successMessages.username && <div className="success-message" style={{ display: 'block' }}>✓ Nombre de usuario disponible</div>}
        </div>

        <div className={getFormGroupClass('password')}>
          <label htmlFor="password">Contraseña *</label>
          <input
            type="password"
            id="password"
            name="password"
            placeholder="Crea una contraseña segura"
            value={formData.password}
            onChange={handleInputChange}
            required
          />
          {errors.password && errors.password !== 'invalid' && (
            <div className="error-message" style={{ display: 'block' }}>{errors.password}</div>
          )}
          <div className="password-requirements">
            <h4>Requisitos de la contraseña:</h4>
            {renderRequirement(passwordRequirements.length, 'Al menos 8 caracteres')}
            {renderRequirement(passwordRequirements.uppercase, 'Una letra mayúscula')}
            {renderRequirement(passwordRequirements.lowercase, 'Una letra minúscula')}
            {renderRequirement(passwordRequirements.number, 'Un número')}
          </div>
        </div>

        <div className={getFormGroupClass('confirmPassword')}>
          <label htmlFor="confirmPassword">Confirmar contraseña *</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            placeholder="Repite tu contraseña"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
          />
          {errors.confirmPassword && <div className="error-message" style={{ display: 'block' }}>{errors.confirmPassword}</div>}
          {successMessages.confirmPassword && <div className="success-message" style={{ display: 'block' }}>✓ Las contraseñas coinciden</div>}
        </div>

        {submitError && <p className="error-message" style={{ display: 'block', marginBottom: '1rem' }}>{submitError}</p>}

        <button type="submit" className="login-btn" disabled={isLoading}>
          {isLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
        </button>
      </form>

      <div className="login-section">
        <p>¿Ya tienes una cuenta?</p>
        <Link to="/iniciarsesion" className="register-btn">Iniciar Sesión</Link>
      </div>
    </div>
  );
};

export default Register;
